import { useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { useParams } from 'react-router';
import { colors } from '@/styles';
import { PALLET_STATUSES, PALLET_STATUS_LABELS } from '@/constants/manifestStatuses';
import {
    INSERT_EXCEPTION,
    INSERT_EXCEPTION_EVENT,
    UPDATE_EXCEPTION,
} from '@/components/OrderDetailsPage/graphql/mutations';
import { useExceptionCallbacks } from '@/components/OrderDetailsPage/hooks';
import { UPDATE_PALLETS_MANY, GET_MANIFEST } from '@/components/Manifest/graphql';
import { buildTrackingEvents } from '@/utilities/trackingEvents';
import { printLabelSelection } from '@/utilities/printManifestLabels';
import { CYCLE_COUNT_ITEM_DETAIL } from './graphql/queries';
import {
    CONSOLIDATED_PALLET_TYPE,
    INBOUND_MANIFEST_TYPES,
    OUTBOUND_MANIFEST_TYPES,
    PALLET_STATUS_COLORS,
} from './constants';
import { findManifestOfType } from './utils';

function useCycleCountItemDetails(itemId, cycleCountLocationId) {
    const { data, loading, refetch: refetchItem } = useQuery(CYCLE_COUNT_ITEM_DETAIL, {
        variables: { item_id: itemId, cycle_count_location_id: cycleCountLocationId },
        skip: !itemId || !cycleCountLocationId,
        fetchPolicy: 'cache-and-network',
        onError: (error) => captureException(error),
    });

    return {
        item: data?.items_by_pk,
        countLocation: data?.cycle_count_locations_by_pk,
        loading,
        refetchItem,
    };
}

// Owns all data, mutations, and callbacks for the cycle-count item page so the
// view stays presentational.
export function useCycleCountItem() {
    const {
        cycle_count_id: cycleCountId,
        cycle_count_location_id: cycleCountLocationId,
        item_id: itemId,
    } = useParams();
    const { item, countLocation, loading, refetchItem } = useCycleCountItemDetails(
        itemId,
        cycleCountLocationId
    );

    const [notification, setNotification] = useState({});
    const [editingException, setEditingException] = useState(null);
    const [relocatingPallet, setRelocatingPallet] = useState(null);
    const [printBusy, setPrintBusy] = useState(false);
    const [activeTab, setActiveTab] = useState('item');
    const [selectedOrderItemIds, setSelectedOrderItemIds] = useState(() => new Set());

    const notifyError = (message) => setNotification({ severity: 'error', message });
    const notifySuccess = (message) => setNotification({ severity: 'success', message });

    const [insertException, { loading: insertExceptionLoading }] = useMutation(INSERT_EXCEPTION, {
        onError: (error) => {
            captureException(error);
            notifyError('Failed to save exception.');
        },
    });
    const [insertExceptionEvent, { loading: insertExceptionEventLoading }] = useMutation(
        INSERT_EXCEPTION_EVENT,
        {
            onError: (error) => {
                captureException(error);
                notifyError('Failed to save exception event.');
            },
        }
    );
    const [updateException, { loading: updateExceptionLoading }] = useMutation(UPDATE_EXCEPTION, {
        onError: (error) => {
            captureException(error);
            notifyError('Failed to update exception.');
        },
    });
    const { createException } = useExceptionCallbacks(
        { order: item?.order, exceptionParams: editingException || {}, route_id: null },
        { insertExceptionEvent, insertException, updateException }
    );

    const [updatePalletsMany] = useMutation(UPDATE_PALLETS_MANY, {
        onError: (error) => {
            if ((error.message || '').includes('pallet_locations_unique')) {
                notifyError('Bin location is already occupied.');
            } else {
                captureException(error);
                notifyError('Failed to relocate pallet.');
            }
        },
    });

    const [fetchInboundManifest] = useLazyQuery(GET_MANIFEST, {
        fetchPolicy: 'network-only',
        onError: (error) => {
            captureException(error);
            notifyError('Failed to load manifest for printing.');
        },
    });

    const order = item?.order;
    const orderItems = order?.itemsByOrderId || [];
    const inboundManifest = findManifestOfType(item, INBOUND_MANIFEST_TYPES);
    const outboundManifest = findManifestOfType(item, OUTBOUND_MANIFEST_TYPES);
    const palletStatus = item?.pallet?.warehouse_status || PALLET_STATUSES.NOT_RECEIVED;

    const openInManifest = (manifest) => {
        if (!manifest?.manifest_id) return;
        window.open(`/manifests/${manifest.manifest_id}`, '_blank', 'noopener,noreferrer');
    };

    const onRelocate = () => {
        if (!item?.pallet?.pallet_id) {
            notifyError('This item is not on a pallet yet.');
            return;
        }
        setRelocatingPallet(item.pallet);
    };

    const onMarkException = () => {
        if (!order?.order_id) {
            notifyError('Cannot report exception — order missing.');
            return;
        }
        // `type` prop seeds exception.reported_at (a DB-check-constrained stage,
        // not the exception's own type). 'PICKUP' matches how Manifest opens it.
        setEditingException({ type: 'PICKUP', items: [item], order });
    };

    const runPrint = async ({ itemIds, palletIds }) => {
        if (!inboundManifest?.manifest_id) {
            notifyError('No inbound manifest linked to this item; cannot print label.');
            return;
        }
        setPrintBusy(true);
        try {
            const result = await fetchInboundManifest({
                variables: { manifest_id: inboundManifest.manifest_id },
            });
            const fullManifest = result?.data?.manifest;
            if (!fullManifest) {
                notifyError('Could not load manifest for printing.');
                return;
            }
            const printed = await printLabelSelection({ manifest: fullManifest, itemIds, palletIds });
            if (!printed) {
                notifyError('Nothing to print — the item or pallet was not found on the inbound manifest.');
            }
        } catch (error) {
            captureException(error);
            notifyError('Print failed. Please try again.');
        } finally {
            setPrintBusy(false);
        }
    };

    const submitException = ({ exception }) =>
        createException(exception).then((result) => {
            // Hook-level onError swallows failures; the promise still resolves,
            // so gate success on the actual returning rows.
            const created = result?.data?.created?.returning;
            if (created && created.length > 0) {
                setEditingException(null);
                notifySuccess('Exception saved.');
                refetchItem?.();
            } else {
                const gqlErrors = result?.errors || result?.error?.graphQLErrors;
                const netError = result?.error?.networkError;
                const msg =
                    gqlErrors?.[0]?.message ||
                    gqlErrors?.[0]?.extensions?.internal?.error?.message ||
                    netError?.message ||
                    JSON.stringify(gqlErrors?.[0] || netError || result || {});
                notifyError(`Exception not saved: ${msg}`);
            }
        });

    const storePallets = async (pallets) => {
        const result = await updatePalletsMany({
            variables: {
                updates: pallets.map((pallet) => ({
                    where: { pallet_id: { _eq: pallet.pallet_id } },
                    _set: {
                        warehouse_location: pallet.warehouse_location,
                        warehouse_status: PALLET_STATUSES.STORED,
                        is_pool: pallet.is_pool,
                    },
                })),
                // Relocate, not put-away: the pallet was already stored. Scoped to this page's single
                // item — on a CONSOLIDATED pallet its siblings are not loaded here.
                trackingEvents: buildTrackingEvents({
                    items: [{ item_id: item?.item_id, order_id: order?.order_id }],
                    eventType: 'ITEM_RELOCATED_IN_WAREHOUSE',
                }),
            },
        });
        if (result?.data) {
            notifySuccess('Pallet relocated.');
            refetchItem?.();
        }
        return result;
    };

    const toggleOrderItem = (id, checked) =>
        setSelectedOrderItemIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });

    const setAllOrderItems = (checked) =>
        setSelectedOrderItemIds(checked ? new Set(orderItems.map((i) => i.item_id)) : new Set());

    return {
        ids: { cycleCountId, cycleCountLocationId },
        item,
        countLocation,
        loading,
        order,
        orderItems,
        inboundManifest,
        outboundManifest,
        manifestNumber: inboundManifest?.manifest_number || outboundManifest?.manifest_number,
        isConsolidatedPallet: item?.pallet?.type === CONSOLIDATED_PALLET_TYPE,
        palletStatusLabel: PALLET_STATUS_LABELS[palletStatus] || 'Not Received',
        palletStatusColor: PALLET_STATUS_COLORS[palletStatus] || colors.greys[2],
        activeTab,
        setActiveTab,
        selectedOrderItemIds,
        toggleOrderItem,
        setAllOrderItems,
        notification,
        setNotification,
        printBusy,
        editingException,
        exceptionLoading:
            insertExceptionLoading || insertExceptionEventLoading || updateExceptionLoading,
        relocatingPallet,
        onMarkException,
        onRelocate,
        openInManifest,
        runPrint,
        submitException,
        submitExceptionCreateAnother: ({ exception }) => createException(exception),
        closeException: () => setEditingException(null),
        storePallets,
        storePalletsInit: () => setRelocatingPallet(null),
        relocateOnError: (message) => (error) => {
            captureException(error);
            notifyError(message || 'Something went wrong.');
        },
        notifyError,
    };
}
